import { useEffect, useMemo, useState } from "react";
import {
  AppButton,
  FormField,
} from "../../components/ui";
import {
  Autocomplete,
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ClioService,
  ClioTaskTypeResponse,
  ClioTasksInfo,
  TaskTypes,
  UsersModel,
} from "../../api";
import { ClioTaskAssigneeSelect } from "../../components/shared/ClioSelects";

export type ClioTaskInfoDrawerProps = {
  open: boolean;
  mode: "create" | "edit";
  task: ClioTasksInfo | null;
  users?: UsersModel[];
  /** Task type IDs (enum names) already used by other templates. */
  usedTaskTypes?: string[];
  onClose: () => void;
  onSave: (task: ClioTasksInfo) => void | Promise<void>;
};

const EMPTY_TASK: ClioTasksInfo = {
  name: "",
  description: "",
  dueDays: 5,
  assignee: "",
  isCalendarDate: false,
  taskType: undefined,
};

export default function ClioTaskInfoDrawer({
  open,
  mode,
  task,
  users,
  usedTaskTypes = [],
  onClose,
  onSave,
}: ClioTaskInfoDrawerProps) {
  const [formTask, setFormTask] = useState<ClioTasksInfo>(EMPTY_TASK);
  const [taskTypes, setTaskTypes] = useState<ClioTaskTypeResponse[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormTask(
      mode === "edit" && task
        ? { ...task }
        : { ...EMPTY_TASK }
    );
  }, [open, mode, task]);

  useEffect(() => {
    if (!open) return;
    setLoadingTypes(true);
    ClioService.getApiClioManagedTaskTypes()
      .then((types) => setTaskTypes(types ?? []))
      .catch(() => setTaskTypes([]))
      .finally(() => setLoadingTypes(false));
  }, [open]);

  const selectableTypes = useMemo(() => {
    const current = formTask.taskType ? String(formTask.taskType) : null;
    return taskTypes.filter((t) => {
      if (!t.inAppEnum || !t.enumName) return false;
      if (t.enabled === false && t.enumName !== current) return false;
      if (usedTaskTypes.includes(t.enumName) && t.enumName !== current) {
        return false;
      }
      return true;
    });
  }, [taskTypes, usedTaskTypes, formTask.taskType]);

  const selectedType =
    selectableTypes.find(
      (t) => t.enumName === String(formTask.taskType ?? "")
    ) ?? null;

  const handleChange = (field: keyof ClioTasksInfo, value: unknown) => {
    setFormTask((prev) => ({ ...prev, [field]: value }));
  };

  const canSave =
    !!formTask.name?.trim() &&
    !!formTask.taskType &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        ...formTask,
        name: formTask.name?.trim(),
        taskType: formTask.taskType as TaskTypes,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {mode === "create"
              ? "Add task template"
              : `Edit task template${formTask.id != null ? ` #${formTask.id}` : ""}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Local automation template linked to a Clio task type
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        <Stack spacing={2.5}>
          <FormField
            label="Name"
            value={formTask.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            required
          />

          <Autocomplete
            options={selectableTypes}
            loading={loadingTypes}
            value={selectedType}
            onChange={(_, option) =>
              handleChange("taskType", option?.enumName ?? undefined)
            }
            getOptionLabel={(option) =>
              option.name
                ? `${option.name} (${option.id})`
                : String(option.id)
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Task type"
                required
                size="small"
                helperText={
                  loadingTypes
                    ? "Loading Clio task types…"
                    : "Only app-mapped Clio types that are not already used"
                }
              />
            )}
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <FormField
              label="Due Days"
              type="number"
              value={formTask.dueDays ?? ""}
              onChange={(e) =>
                handleChange(
                  "dueDays",
                  e.target.value === "" ? null : +e.target.value
                )
              }
              sx={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formTask.isCalendarDate}
                  onChange={(e) =>
                    handleChange("isCalendarDate", e.target.checked)
                  }
                />
              }
              label="Calendar Date"
              sx={{ mt: 1 }}
            />
          </Stack>

          <FormField
            label="Description"
            multiline
            minRows={3}
            value={formTask.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />

          <ClioTaskAssigneeSelect
            value={formTask.assignee || ""}
            onChange={(assignee) => handleChange("assignee", assignee)}
            users={users}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <AppButton variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {saving ? "Saving…" : "Save"}
          </AppButton>
        </Stack>
      </Box>
    </Drawer>
  );
}
